import { ICustomerDetails } from "@interface/customerDetails/ICustomerDetails";
import { format, parse } from "date-fns";
import { mapContactPermission } from "@features/customerDetails/mappers/mapContactPermission";
import { EPermissions, ICustomerDetailsForm } from "@features/customerDetails/ICustomerDetailsForm";
import { ICart } from "@features/cart/ICart";
import { EEirSalesSimType } from "@mappers/getCartMapper";
import { IAddress } from "@interface/customerDetails/IAddress";
import { removeWhitespace } from "@utils/stringUtils";
import { checkCartOfferByServiceGroup, hasOfferBroadband, selectOfferInCartByServiceGroup } from "@utils/offerUtils";
import { EAppointmentType, EServiceGroup } from "@constants";

export const formatUserDate = (date: string) => format(parse(date, "yyyy-MM-dd", new Date()), "dd/MM/yyyy");

export const hasAtLeastOnePSimInCart = (cart: ICart | undefined): boolean =>
    cart ? cart.offers.some((offer) => offer.simType === EEirSalesSimType.PSIM) : false;

export const shouldCallPutDeliveryAddress = (cart: ICart | undefined): boolean => {
    if (!cart?.offers.length) {
        return false;
    }

    const hasMobileOffer = cart.offers.some((offer) => checkCartOfferByServiceGroup(offer, EServiceGroup.MOBILE));

    return hasMobileOffer || hasOfferBroadband(cart.offers);
};

export const hasOnlyESimInCart = (cart: ICart | undefined): boolean =>
    cart ? cart.offers.every((offer) => offer.simType === EEirSalesSimType.ESIM) : false;

export const cartRequiresSimDeliveryAddress = (cart: ICart | undefined): boolean => {
    if (!cart) {
        return false;
    }

    if (hasAtLeastOnePSimInCart(cart)) {
        return true;
    }

    const broadbandOffer = selectOfferInCartByServiceGroup(cart, EServiceGroup.SA_BB);
    if (!broadbandOffer) {
        return false;
    }

    if (broadbandOffer.prequal?.isPreOrder) {
        return false;
    }

    return broadbandOffer.appointment?.type !== EAppointmentType.FULL_INSTALL;
};

const getDeliveryAddress = (
    formData: ICustomerDetailsForm,
    displayDeliveryAddress: boolean,
    broadbandInstallationAddress?: IAddress
) => {
    if (displayDeliveryAddress) {
        const deliveryAddressForm = formData.deliveryAddress;

        return {
            addressLine1: deliveryAddressForm?.addressLine1 ?? "",
            addressLine2: deliveryAddressForm?.addressLine2 ?? undefined,
            addressLine3: deliveryAddressForm?.addressLine3 ?? undefined,
            county: deliveryAddressForm?.county ?? "",
            eircode: removeWhitespace(deliveryAddressForm?.code),
            town: deliveryAddressForm?.town ?? "",
        };
    }

    if (broadbandInstallationAddress) {
        return broadbandInstallationAddress;
    }

    return {
        addressLine1: formData.billingAddress.addressLine1 || "",
        addressLine2: formData.billingAddress.addressLine2 ?? undefined,
        addressLine3: formData.billingAddress.addressLine3 ?? undefined,
        county: formData.billingAddress.county || "",
        eircode: removeWhitespace(formData.billingAddress.code),
        town: formData.billingAddress.town || "",
    };
};

export const getCustomerDetailsRequest = (
    formData: ICustomerDetailsForm,
    displayDeliveryAddress: boolean,
    customerDetails: ICustomerDetails,
    isAuthenticated: boolean,
    isAllESim: boolean,
    broadbandInstallationAddress?: IAddress
): ICustomerDetails => {
    const parsedBirthDate = isAuthenticated
        ? parse(customerDetails.birthDate, "yyyy-MM-dd", new Date())
        : parse(formData.birthDate, "dd/MM/yyyy", new Date());
    const birthDateFormatted = format(parsedBirthDate, "yyyy-MM-dd");

    const billingAddress = isAuthenticated
        ? customerDetails.billingAddress
        : broadbandInstallationAddress ?? {
              addressLine1: formData.billingAddress.addressLine1,
              addressLine2: formData.billingAddress?.addressLine2,
              addressLine3: formData.billingAddress?.addressLine3,
              county: formData.billingAddress.county,
              eircode: removeWhitespace(formData.billingAddress.code),
              town: formData.billingAddress.town,
          };

    const getDeliveryAddressForRequest = () => {
        if (displayDeliveryAddress) {
            return getDeliveryAddress(formData, displayDeliveryAddress, broadbandInstallationAddress);
        }

        if (isAuthenticated) {
            if (broadbandInstallationAddress) {
                return getDeliveryAddress(formData, false, broadbandInstallationAddress);
            }

            return customerDetails.deliveryAddress;
        }

        if (isAllESim) {
            return billingAddress;
        }

        return getDeliveryAddress(formData, displayDeliveryAddress, broadbandInstallationAddress);
    };

    const deliveryAddress = getDeliveryAddressForRequest();

    return {
        billingAddress,
        deliveryAddress,
        contactPermission: isAuthenticated
            ? customerDetails.contactPermission
            : mapContactPermission(formData.contactPermissions),
        email: isAuthenticated ? customerDetails.email ?? "" : formData.email,
        mobileNumber: formData.mobileNumber ?? "",
        firstName: isAuthenticated ? customerDetails.firstName ?? "" : formData.firstName,
        lastName: isAuthenticated ? customerDetails.lastName ?? "" : formData.lastName,
        birthDate: birthDateFormatted,
    };
};

const createStandardPermissions = (overrides: Partial<Record<keyof typeof EPermissions, boolean>>) => [
    {
        permission: "ALLOW_EMAIL_CONTACT",
        name: "Email",
        enabled: overrides.ALLOW_EMAIL_CONTACT ?? false,
    },
    {
        permission: "ALLOW_SMS_CONTACT",
        name: "SMS",
        enabled: overrides.ALLOW_SMS_CONTACT ?? false,
    },
    {
        permission: "ALLOW_PHONE_CONTACT",
        name: "Phone",
        enabled: overrides.ALLOW_PHONE_CONTACT ?? false,
    },
    {
        permission: "ALLOW_DIRECT_MAIL_CONTACT",
        name: "Direct mail",
        enabled: overrides.ALLOW_DIRECT_MAIL_CONTACT ?? false,
    },
];

export const createAllPermissionGroups = (
    activeCustomerOverrides: Partial<Record<keyof typeof EPermissions, boolean>> = {},
    noLongerCustomerOverrides: Partial<Record<keyof typeof EPermissions, boolean>> = {},
    thirdPartyOverrides: Partial<Record<keyof typeof EPermissions, boolean>> = {}
) => [
    {
        name: "Active customer",
        permissionGroup: "ACTIVE_CUSTOMER",
        permissions: createStandardPermissions(activeCustomerOverrides),
    },
    {
        name: "No longer a customer",
        permissionGroup: "NO_LONGER_CUSTOMER",
        permissions: createStandardPermissions(noLongerCustomerOverrides),
    },
    {
        name: "3rd party",
        permissionGroup: "ALLOW_3RD_PARTIES",
        permissions: createStandardPermissions(thirdPartyOverrides),
    },
];
